import React, { useCallback, useEffect } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Uploading } from './components';

import { RteState } from '../types';

import { asyncCallWithTimeout } from '../utils';

import { useFormWithoutBackButton } from '../../../../BackButtonContext';

import * as api from '../../../../../api';
import * as logger from '../../../../../logger';

const RTE_TIMEOUT_MS = 10000;
const RTE_TIMEOUT_REASON = 'RTE Timeout';

interface VerifyingInsurancePageProps extends WithTranslation {
  rteState: RteState;
  setRteState: React.Dispatch<React.SetStateAction<RteState>>;
  onComplete: (
    patientInsuranceInformationId: string,
    patientUploadId: string,
    rteState: RteState,
  ) => Promise<void>;
  onTimeout: () => Promise<void>;
  patientInsuranceInformationId?: string;
  patientUploadId?: string;
  encounter: api.Encounter.Encounter;
}

const VerifyingInsurancePage = ({
  rteState,
  setRteState,
  onComplete,
  onTimeout,
  patientInsuranceInformationId,
  patientUploadId,
  encounter,
  t,
}: VerifyingInsurancePageProps) => {
  useFormWithoutBackButton();

  const checkRealTimeEligibility = useCallback(async (): Promise<void> => {
    if (!patientInsuranceInformationId) {
      throw new Error(
        'a patientInsuranceInformationId is required to check RTE',
      );
    }

    let apiResponse:
      | api.PatientRealTimeEligibility.RealTimeEligibilityResults
      | null
      | undefined = null;
    try {
      apiResponse = await asyncCallWithTimeout(
        api.client.RealTimeEligibility.checkPatientRealTimeEligibility(
          patientInsuranceInformationId,
          encounter.id,
        ),
        RTE_TIMEOUT_MS,
        RTE_TIMEOUT_REASON,
      );
    } catch (e) {
      logger.error(e, 'Error response when checking real time eligibility');
      if (e === RTE_TIMEOUT_REASON) {
        await onTimeout();
        apiResponse = {
          status: api.PatientRealTimeEligibility.EligibilityResponse.Error,
          groupNumber: null,
          coverageStartDate: null,
          payor: null,
          patientRelationshipToSubscriber: null,
          subscriber: null,
        };
      }
    }

    const response = apiResponse ?? null;

    setRteState((prev) => ({
      ...prev,
      completed: true,
      results: response,
      error:
        response &&
        response.status !==
          api.PatientRealTimeEligibility.EligibilityResponse.Active
          ? response.status
          : null,
    }));

    await onComplete(patientInsuranceInformationId, patientUploadId!, {
      ...rteState,
      completed: true,
      results: response,
      error:
        response &&
        response.status !==
          api.PatientRealTimeEligibility.EligibilityResponse.Active
          ? response.status
          : api.PatientRealTimeEligibility.EligibilityResponse.Error,
    });
  }, [
    patientInsuranceInformationId,
    setRteState,
    onComplete,
    patientUploadId,
    rteState,
    encounter.id,
    onTimeout,
  ]);

  useEffect(() => {
    if (rteState.started) {
      return;
    }
    setRteState((prev) => ({ ...prev, started: true }));

    checkRealTimeEligibility();
  }, [rteState.started, setRteState, checkRealTimeEligibility]);

  const completed = rteState.completed && !rteState.error;
  const message = completed
    ? t('verificationSuccessful')
    : t('verifyingInsurance');

  return <Uploading message={message} completed={completed} />;
};

export default withTranslation('processing')(VerifyingInsurancePage);
